"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    investees: string;
    insideFund: string;
    inYourHands: string;
    equity: string;
    bond: string;
    dividend: string;
    interest: string;
    fund: string;
    receivesIncome: string;
    payOut: string;
    distribution: string;
    payFromIncome: string;
    tooMuchWarning: string;
    reinvest: string;
    noCash: string;
    staysInside: string;
    keepInside: string;
    easierToCompound: string;
    distributingTitle: string;
    distributingDescription: string;
    reinvestingTitle: string;
    reinvestingDescription: string;
  }
> = {
  ja: {
    investees: "投資先",
    insideFund: "ファンド内",
    inYourHands: "投資家の手元",
    equity: "株式",
    bond: "債券",
    dividend: "配当",
    interest: "利息",
    fund: "投資信託",
    receivesIncome: "運用収益を受け取る",
    payOut: "外へ出す",
    distribution: "分配金",
    payFromIncome: "運用収益から分配金を出す",
    tooMuchWarning: "分配金が多すぎると、純資産と基準価額が下がりやすい",
    reinvest: "再投資",
    noCash: "現金は来ない",
    staysInside: "ファンド内に残る",
    keepInside: "運用収益をファンド内に残す",
    easierToCompound: "現金では受け取らず、再投資しやすい",
    distributingTitle: "分配型",
    distributingDescription: "運用収益から投資家へ現金を出す設計です。支出が大きすぎないかを見る必要があります。",
    reinvestingTitle: "ファンド内再投資型（無分配）",
    reinvestingDescription: "運用収益をファンド内に残し、投資先へ再投資しやすい設計です。",
  },
  en: {
    investees: "What it invests in",
    insideFund: "Inside the fund",
    inYourHands: "In your hands",
    equity: "Equities",
    bond: "Bonds",
    dividend: "Dividends",
    interest: "Interest",
    fund: "Mutual fund",
    receivesIncome: "Receives the income",
    payOut: "Paid out",
    distribution: "Distribution",
    payFromIncome: "Pays distributions out of income",
    tooMuchWarning: "Distribute too much and both net assets and NAV tend to fall",
    reinvest: "Reinvest",
    noCash: "No cash arrives",
    staysInside: "Stays inside the fund",
    keepInside: "Keeps the income inside the fund",
    easierToCompound: "You receive no cash, which makes reinvesting easier",
    distributingTitle: "Distributing type",
    distributingDescription:
      "Designed to pay cash out of income to investors. You need to watch whether the payout is too large.",
    reinvestingTitle: "Internally reinvesting type (no distribution)",
    reinvestingDescription: "Designed to keep income inside the fund, making it easier to reinvest.",
  },
};

// ── Colors ─────────────────────────────────────────────────────────

const C = {
  amber: COLOR.amber,
  sky: COLOR.sky,
  rose: COLOR.rose,
  emerald: COLOR.emerald,
};

// ── SVG primitives ─────────────────────────────────────────────────

function Box({
  x,
  y,
  w,
  h,
  color,
  label,
  sub,
  labelSize = 11,
  subSize = 8,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  label: string;
  sub?: string;
  labelSize?: number;
  subSize?: number;
}) {
  const hasSub = Boolean(sub);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={10} fill={color} fillOpacity={0.08} stroke={color} strokeWidth="1.2" />
      <text
        x={x + w / 2}
        y={hasSub ? y + h / 2 - 4 : y + h / 2 + 4}
        textAnchor="middle"
        fontSize={labelSize}
        fill={color}
        fontWeight="700"
      >
        {label}
      </text>
      {sub && (
        <text x={x + w / 2} y={y + h / 2 + 12} textAnchor="middle" fontSize={subSize} fill="currentColor">
          {sub}
        </text>
      )}
    </g>
  );
}

function Zone({ x, y, w, h, label }: { x: number; y: number; w: number; h: number; label: string }) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={14}
        fill="currentColor"
        fillOpacity={0.02}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.1}
        strokeDasharray="6 5"
      />
      <text
        x={x + w / 2}
        y={y + 20}
        textAnchor="middle"
        fontSize="10"
        fill="currentColor"
        fontWeight="700"
        fillOpacity={0.3}
      >
        {label}
      </text>
    </g>
  );
}

function HArrow({
  x1,
  x2,
  y,
  color,
  label,
  labelDy = -10,
  dashed,
}: {
  x1: number;
  x2: number;
  y: number;
  color: string;
  label?: string;
  labelDy?: number;
  dashed?: boolean;
}) {
  const forward = x2 > x1;
  const endX = forward ? x2 - 8 : x2 + 8;
  const head = forward
    ? `M ${x2 - 12} ${y - 4} L ${x2 - 4} ${y} L ${x2 - 12} ${y + 4}`
    : `M ${x2 + 12} ${y - 4} L ${x2 + 4} ${y} L ${x2 + 12} ${y + 4}`;

  return (
    <g>
      <line
        x1={x1}
        y1={y}
        x2={endX}
        y2={y}
        stroke={color}
        strokeWidth="1.5"
        strokeDasharray={dashed ? "4 3" : undefined}
      />
      <path d={head} fill="none" stroke={color} strokeWidth="1.5" />
      {label && (
        <text x={(x1 + x2) / 2} y={y + labelDy} textAnchor="middle" fontSize="8.5" fill={color} fontWeight="600">
          {label}
        </text>
      )}
    </g>
  );
}

function Badge({
  x,
  y,
  w,
  h,
  color,
  text,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  text: string;
}) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={6} fill={color} fillOpacity={0.12} stroke={color} strokeWidth="1" />
      <text x={x + w / 2} y={y + h / 2 + 3.5} textAnchor="middle" fontSize="8" fill={color} fontWeight="700">
        {text}
      </text>
    </g>
  );
}

// ── Layout ─────────────────────────────────────────────────────────

const VW = 520;
const VH = 220;

const ZONE_Y = 20;
const ZONE_H = 148;

const Z1 = { x: 12, y: ZONE_Y, w: 96, h: ZONE_H };
const Z2 = { x: 122, y: ZONE_Y, w: 236, h: ZONE_H };
const Z3 = { x: 372, y: ZONE_Y, w: 136, h: ZONE_H };

const BOX_H = 36;
const STOCK = { x: Z1.x + 12, y: ZONE_Y + 38, w: 72, h: BOX_H };
const BOND = { x: Z1.x + 12, y: STOCK.y + 48, w: 72, h: BOX_H };

const FUND = { x: Z2.x + 46, y: ZONE_Y + 34, w: 144, h: 84 };

// ── Distribution route ─────────────────────────────────────────────

function DistributionRoute() {
  const t = STRINGS[useDocLocale()];
  const stockCy = STOCK.y + STOCK.h / 2;
  const bondCy = BOND.y + BOND.h / 2;
  const fundCy = FUND.y + FUND.h / 2;

  const hand = { x: Z3.x + 12, y: fundCy - BOX_H / 2, w: 112, h: BOX_H };
  const cashBadge = { x: FUND.x + FUND.w + 8, y: fundCy - 32, w: 82, h: 20 };

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <Zone {...Z1} label={t.investees} />
      <Zone {...Z2} label={t.insideFund} />
      <Zone {...Z3} label={t.inYourHands} />

      <Box x={STOCK.x} y={STOCK.y} w={STOCK.w} h={STOCK.h} color={C.amber} label={t.equity} />
      <Box x={BOND.x} y={BOND.y} w={BOND.w} h={BOND.h} color={C.amber} label={t.bond} />

      <HArrow x1={STOCK.x + STOCK.w} x2={FUND.x} y={stockCy} color={C.amber} label={t.dividend} />
      <HArrow x1={BOND.x + BOND.w} x2={FUND.x} y={bondCy} color={C.amber} label={t.interest} />

      <Box
        x={FUND.x}
        y={FUND.y}
        w={FUND.w}
        h={FUND.h}
        color={C.sky}
        label={t.fund}
        sub={t.receivesIncome}
        labelSize={12}
      />

      <HArrow x1={FUND.x + FUND.w} x2={hand.x} y={fundCy} color={C.amber} />

      <Badge x={cashBadge.x} y={cashBadge.y} w={cashBadge.w} h={cashBadge.h} color={C.rose} text={t.payOut} />

      <Box x={hand.x} y={hand.y} w={hand.w} h={hand.h} color={C.amber} label={t.distribution} labelSize={10} />

      <text x={VW / 2} y={VH - 20} textAnchor="middle" fontSize="9.5" fill={C.rose} fontWeight="700">
        {t.payFromIncome}
      </text>
      <text x={VW / 2} y={VH - 6} textAnchor="middle" fontSize="8" fill="currentColor">
        {t.tooMuchWarning}
      </text>
    </svg>
  );
}

// ── Reinvestment route ─────────────────────────────────────────────

function ReinvestmentRoute() {
  const t = STRINGS[useDocLocale()];
  const stockCy = STOCK.y + STOCK.h / 2;
  const bondCy = BOND.y + BOND.h / 2;
  const investorCx = Z3.x + Z3.w / 2;

  const reinvestStockY = stockCy + 18;
  const reinvestBondY = bondCy - 18;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <Zone {...Z1} label={t.investees} />
      <Zone {...Z2} label={t.insideFund} />
      <Zone {...Z3} label={t.inYourHands} />

      <Box x={STOCK.x} y={STOCK.y} w={STOCK.w} h={STOCK.h} color={C.amber} label={t.equity} />
      <Box x={BOND.x} y={BOND.y} w={BOND.w} h={BOND.h} color={C.amber} label={t.bond} />

      <HArrow x1={STOCK.x + STOCK.w} x2={FUND.x} y={stockCy} color={C.amber} label={t.dividend} />
      <HArrow x1={BOND.x + BOND.w} x2={FUND.x} y={bondCy} color={C.amber} label={t.interest} />

      <Box
        x={FUND.x}
        y={FUND.y}
        w={FUND.w}
        h={FUND.h}
        color={C.sky}
        label={t.fund}
        sub={t.receivesIncome}
        labelSize={12}
      />

      {/* Reinvestment arrows */}
      <HArrow x1={FUND.x} x2={STOCK.x + STOCK.w} y={reinvestStockY} color={C.emerald} label={t.reinvest} labelDy={-9} />
      <HArrow x1={FUND.x} x2={BOND.x + BOND.w} y={reinvestBondY} color={C.emerald} label={t.reinvest} labelDy={-9} />

      {/* Investor side */}
      <text
        x={investorCx}
        y={ZONE_Y + ZONE_H / 2 - 2}
        textAnchor="middle"
        fontSize="11"
        fill="currentColor"
        fontWeight="600"
        fillOpacity={0.2}
      >
        {t.noCash}
      </text>
      <text
        x={investorCx}
        y={ZONE_Y + ZONE_H / 2 + 16}
        textAnchor="middle"
        fontSize="8.5"
        fill="currentColor"
        fillOpacity={0.15}
      >
        {t.staysInside}
      </text>

      <text x={VW / 2} y={VH - 20} textAnchor="middle" fontSize="9.5" fill={C.emerald} fontWeight="700">
        {t.keepInside}
      </text>
      <text x={VW / 2} y={VH - 6} textAnchor="middle" fontSize="8" fill="currentColor">
        {t.easierToCompound}
      </text>
    </svg>
  );
}

// ── Exports ─────────────────────────────────────────────────────────

export function DocFundDistributionRouteDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.distributingTitle}
      description={t.distributingDescription}
    >
      <DistributionRoute />
    </IllustrationCard>
  );
}

export function DocFundReinvestmentRouteDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.reinvestingTitle}
      description={t.reinvestingDescription}
    >
      <ReinvestmentRoute />
    </IllustrationCard>
  );
}
